import { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from '@eti/styles';
import { usePrevious, useScrollTo } from '@eti/utils';
import { TransitionGroup as CSSTransitionGroup, CSSTransition } from 'react-transition-group';
import { useProperty } from '@eti/providers';
import { getCabins } from '../utils/decks';
import { getSegment } from '../utils/segments';
import { SEAT_HEIGHT, SEAT_WIDTH, SEATING_PREFERENCE_OPTIONS } from '../utils/constants';
import SeatMapCabin from './SeatMapCabin';
import UnavailableSegment from './UnavailableSegment';
import SeatingPreference from './SeatingPreference';
import { jsx as _jsx } from "react/jsx-runtime";
const deckWrapperStyles = deckHeight => css`
  border-radius: 0 0 0.1875rem 0.1875rem;
  height: ${deckHeight && `${deckHeight}`};
  opacity: 1;
  overflow: auto;
  position: relative;
  text-size-adjust: none; /* prevent webkit from resizing text to fit */
  top: 0;
  -webkit-touch-callout: none; /* prevent callout to copy image, etc when tap to hold */
  user-select: none; /* prevent copy paste, to allow, change 'none' to 'text' */
  visibility: visible;
  width: 100%;
`;
const deckTransitionStyles = css`
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;
const transitionWrapperStyles = css`
  position: relative;

  html[dir='ltr'] & {
    &.slide-right {
      .deck-enter {
        opacity: 0.3;
        transform: translateX(100%);
      }
      .deck-enter-active {
        opacity: 1;
        transform: translateX(0);
        transition: all 0.9s;
      }
      .deck-exit {
        opacity: 1;
        transform: translateX(0);
      }
      .deck-exit-active {
        opacity: 0.3;
        transform: translateX(-100%);
        transition: all 0.9s;
      }
    }
    &.slide-left {
      .deck-enter {
        opacity: 0.3;
        transform: translateX(-100%);
      }
      .deck-enter-active {
        opacity: 1;
        transform: translateX(0);
        transition: all 0.9s;
      }
      .deck-exit {
        opacity: 1;
        transform: translateX(0);
      }
      .deck-exit-active {
        opacity: 0.3;
        transform: translateX(100%);
        transition: all 0.9s;
      }
    }
  }

  html[dir='rtl'] & {
    &.slide-right {
      .deck-enter {
        opacity: 0.3;
        transform: translateX(-100%);
      }
      .deck-enter-active {
        opacity: 1;
        transform: translateX(0);
        transition: all 0.9s;
      }
      .deck-exit {
        opacity: 1;
        transform: translateX(0);
      }
      .deck-exit-active {
        opacity: 0.3;
        transform: translateX(100%);
        transition: all 0.9s;
      }
    }
    &.slide-left {
      .deck-enter {
        opacity: 0.3;
        transform: translateX(100%);
      }
      .deck-enter-active {
        opacity: 1;
        transform: translateX(0);
        transition: all 0.9s;
      }
      .deck-exit {
        opacity: 1;
        transform: translateX(0);
      }
      .deck-exit-active {
        opacity: 0.3;
        transform: translateX(-100%);
        transition: all 0.9s;
      }
    }
  }
`;
const generateKey = index => `cabin_${index}`;
const SeatMapDeck = _ref => {
  let {
    active: [activeSegment, activeTraveler],
    segments,
    onChange,
    isFromSummary,
    direction,
    selectedDeck = {
      value: 'mainDeck'
    },
    selectedSeats,
    travelers
  } = _ref;
  const {
    p
  } = useProperty();
  const targetRef = useRef(null);
  const deckRef = useRef(null);
  const [deckHeight, setDeckHeight] = useState(0);
  const {
    scroll
  } = useScrollTo(targetRef);
  const previousActiveTraveler = usePrevious(activeTraveler);
  const isSeatingPage = p('TravelerDetails.DynamicFlow.Enabled');
  const currentSegment = getSegment(segments, activeSegment);
  const cabins = useMemo(() => getCabins(currentSegment, selectedDeck), [currentSegment, selectedDeck]);
  const rows = cabins.flatMap(cabin => cabin.rows);
  const seatSelectedForActiveTraveler = selectedSeats && selectedSeats[activeTraveler];
  const isSeatingPreferenceAvailable = currentSegment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE || currentSegment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE;
  const getSeatCoords = selectedSeat => {
    let [left, top] = [0, 0];
    const columns = `ABCDEFGHIJKLMNOPQRSTUVWXYZ`.split('');
    const seatHasColumn = selectedSeat && selectedSeat.column;
    const seatHasRow = selectedSeat && selectedSeat.row;
    if (seatHasColumn && seatHasRow) {
      const columnIndex = columns.indexOf(selectedSeat.column);
      const rowIndex = rows.findIndex(r => r.rowNumber === selectedSeat.row);
      left = SEAT_WIDTH * (columnIndex + 1);
      top = SEAT_HEIGHT * (rowIndex + 1);
    }
    return {
      left,
      top
    };
  };
  const getAlternativeContent = () => isSeatingPreferenceAvailable ? /*#__PURE__*/_jsx(SeatingPreference, {
    activeSegment: activeSegment,
    onChange: onChange,
    selectedSeats: selectedSeats,
    ...currentSegment
  }) : /*#__PURE__*/_jsx(UnavailableSegment, {});
  const getCoordsForSelectedSeat = activeTravelerForSelectedSeats => {
    if (!activeTravelerForSelectedSeats) {
      const [allAvailableSeats] = cabins === null || cabins === void 0 ? void 0 : cabins.flatMap(cabin => {
        var _cabin$rows;
        return cabin === null || cabin === void 0 || (_cabin$rows = cabin.rows) === null || _cabin$rows === void 0 ? void 0 : _cabin$rows.flatMap(row => row.sellSeatMapSeats.filter(seat => seat.isBookable));
      });
      return allAvailableSeats || {};
    }
    return activeTravelerForSelectedSeats;
  };
  const scrollToSeat = () => {
    const selectedSeat = getCoordsForSelectedSeat(seatSelectedForActiveTraveler);
    const {
      top,
      left
    } = getSeatCoords(selectedSeat);
    scroll('smooth', top, left);
  };
  useEffect(() => {
    const isDifferentTraveler = typeof previousActiveTraveler === 'number' && previousActiveTraveler !== activeTraveler && seatSelectedForActiveTraveler;
    if (isDifferentTraveler) {
      scrollToSeat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTraveler, previousActiveTraveler]);
  useEffect(() => {
    if (isFromSummary) {
      scrollToSeat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromSummary]);
  useEffect(() => {
    scrollToSeat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isSeatingPage) {
      setDeckHeight(`${deckRef.current.getBoundingClientRect().height}px`);
    } else {
      setDeckHeight('100%');
    }
  }, [activeSegment, isSeatingPage]);
  return /*#__PURE__*/_jsx("div", {
    className: deckWrapperStyles(deckHeight),
    ref: targetRef,
    children: /*#__PURE__*/_jsx(CSSTransitionGroup, {
      className: cx(transitionWrapperStyles, {
        'slide-right': direction > 0,
        'slide-left': direction < 0
      }),
      children: /*#__PURE__*/_jsx(CSSTransition, {
        className: deckTransitionStyles,
        classNames: "deck",
        in: true,
        mountOnEnter: false,
        onEntered: scrollToSeat,
        timeout: 900,
        unmountOnExit: true,
        children: /*#__PURE__*/_jsx("div", {
          ref: deckRef,
          children: cabins && cabins.length > 0 ? cabins.map((cabin, index) => /*#__PURE__*/_jsx(SeatMapCabin, {
            activeSegment: activeSegment,
            activeTraveler: activeTraveler,
            carrier: currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.carrier,
            onChange: onChange,
            travelers: travelers,
            ...cabin
          }, generateKey(index))) : getAlternativeContent()
        })
      }, activeSegment)
    })
  });
};
SeatMapDeck.propTypes = {
  active: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  direction: PropTypes.oneOf([-1, 0, 1]),
  isFromSummary: PropTypes.bool,
  onChange: PropTypes.func,
  segments: PropTypes.arrayOf(PropTypes.shape({
    from: PropTypes.shape({
      airport: PropTypes.string,
      city: PropTypes.string
    }),
    to: PropTypes.shape({
      airport: PropTypes.string,
      city: PropTypes.string
    })
  })),
  selectedDeck: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  selectedSeats: PropTypes.arrayOf(PropTypes.object),
  travelers: PropTypes.arrayOf(PropTypes.shape({
    ageType: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }))
};
export default SeatMapDeck;
//# sourceMappingURL=SeatMapDeck.js.map